import { AnimatePresence, motion } from 'motion/react';
import {
  createContext,
  forwardRef,
  useCallback,
  useContext,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type ButtonHTMLAttributes,
  type InputHTMLAttributes,
  type ReactNode,
  type SelectHTMLAttributes,
  type TextareaHTMLAttributes,
} from 'react';
import { createPortal } from 'react-dom';

import { transitions } from '../lib/motion';
import { useI18n } from '../i18n';
import { accentVar, cn, initials, useScrollLock } from '../lib/ui';
import { IconCheck, IconClose } from './icons';

// --- Button -----------------------------------------------------------------

type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: 'primary' | 'secondary' | 'ghost' | 'destructive';
  size?: 'sm' | 'md' | 'lg';
  loading?: boolean;
  fullWidth?: boolean;
  icon?: ReactNode;
};

const BUTTON_VARIANTS: Record<NonNullable<ButtonProps['variant']>, string> = {
  primary: 'text-[var(--accent-ink)] bg-[var(--accent)] hover:bg-[var(--accent-hover)]',
  secondary:
    'bg-[var(--surface-sunken)] text-[var(--ink)] hover:bg-[var(--line)] border border-[var(--line)]',
  ghost: 'text-[var(--ink-secondary)] hover:text-[var(--ink)] hover:bg-[var(--surface-sunken)]',
  destructive: 'text-white bg-[var(--critical)] hover:opacity-90',
};

const BUTTON_SIZES: Record<NonNullable<ButtonProps['size']>, string> = {
  sm: 'h-9 px-3.5 text-[13px] gap-1.5',
  // 44px is Apple's minimum comfortable touch target and it shows.
  md: 'h-11 px-5 text-[15px] gap-2',
  lg: 'h-[52px] px-6 text-[16px] gap-2',
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = 'primary', size = 'md', loading, fullWidth, icon, children, className, ...props },
  ref,
) {
  return (
    <button
      ref={ref}
      className={cn(
        'press inline-flex items-center justify-center whitespace-nowrap rounded-[var(--radius-control)] font-medium',
        'disabled:pointer-events-none disabled:opacity-45',
        BUTTON_VARIANTS[variant],
        BUTTON_SIZES[size],
        fullWidth && 'w-full',
        className,
      )}
      disabled={props.disabled ?? loading}
      {...props}
    >
      {loading ? <Spinner /> : icon}
      {children}
    </button>
  );
});

export function Spinner({ size = 16 }: { size?: number }) {
  return (
    <span
      className="inline-block animate-spin rounded-full border-2 border-current border-t-transparent"
      style={{ width: size, height: size }}
      aria-hidden="true"
    />
  );
}

// --- Surfaces ---------------------------------------------------------------

export function Card({
  children,
  className,
  padded = true,
  ...props
}: { children: ReactNode; padded?: boolean } & React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div className={cn('card', padded && 'p-5', className)} {...props}>
      {children}
    </div>
  );
}

export function SectionHeader({
  title,
  action,
  description,
}: {
  title: string;
  description?: string;
  action?: ReactNode;
}) {
  return (
    // Without min-w-0 / shrink-0 the action was squeezed until its label wrapped
    // — "New ledger" became a two-line button, which looks broken rather than
    // tight. The description is what should give way.
    <div className="mb-3 flex items-end justify-between gap-3 px-1">
      <div className="min-w-0">
        <h2 className="text-[15px] font-semibold tracking-tight text-[var(--ink)]">{title}</h2>
        {description ? (
          <p className="mt-0.5 text-[13px] text-[var(--ink-secondary)]">{description}</p>
        ) : null}
      </div>
      {action ? <div className="shrink-0">{action}</div> : null}
    </div>
  );
}

/** iOS-style grouped list. Rows are separated by hairlines, not borders. */
export function List({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <div className={cn('card overflow-hidden', className)}>
      <div className="divide-y divide-[var(--line)]">{children}</div>
    </div>
  );
}

export function Row({
  label,
  value,
  hint,
  leading,
  trailing,
  onClick,
  as = 'div',
  className,
}: {
  label: ReactNode;
  value?: ReactNode;
  hint?: ReactNode;
  leading?: ReactNode;
  trailing?: ReactNode;
  onClick?: () => void;
  as?: 'div' | 'button';
  className?: string;
}) {
  const Element = onClick ? 'button' : as;
  return (
    <Element
      type={Element === 'button' ? 'button' : undefined}
      onClick={onClick}
      className={cn(
        'flex w-full items-center gap-3 px-4 py-3 text-left',
        onClick && 'press hover:bg-[var(--surface-sunken)]',
        className,
      )}
    >
      {leading}
      <span className="min-w-0 flex-1">
        <span className="block truncate text-[15px] text-[var(--ink)]">{label}</span>
        {hint ? (
          <span className="mt-0.5 block truncate text-[13px] leading-snug text-[var(--ink-secondary)]">
            {hint}
          </span>
        ) : null}
      </span>
      {value ? (
        <span className="shrink-0 text-[15px] text-[var(--ink-secondary)]">{value}</span>
      ) : null}
      {trailing}
    </Element>
  );
}

// --- Form fields ------------------------------------------------------------

export function Field({
  label,
  hint,
  error,
  children,
  optional,
  htmlFor,
}: {
  label: string;
  hint?: string;
  error?: string;
  optional?: boolean;
  htmlFor?: string;
  children: ReactNode;
}) {
  const { t } = useI18n();
  return (
    <label className="block" htmlFor={htmlFor}>
      <span className="mb-1.5 flex items-baseline justify-between gap-2">
        <span className="text-[13px] font-medium text-[var(--ink-secondary)]">{label}</span>
        {optional ? (
          <span className="text-[12px] text-[var(--ink-muted)]">{t('common.optional')}</span>
        ) : null}
      </span>
      {children}
      {error ? (
        <span className="mt-1.5 block text-[12px] text-[var(--critical)]">{error}</span>
      ) : hint ? (
        <span className="mt-1.5 block text-[12px] leading-snug text-[var(--ink-muted)]">
          {hint}
        </span>
      ) : null}
    </label>
  );
}

const CONTROL_CLASS =
  'w-full rounded-[var(--radius-control)] border border-[var(--line)] bg-[var(--surface)] px-3.5 py-2.5 text-[15px] text-[var(--ink)] placeholder:text-[var(--ink-muted)] transition-colors focus:border-[var(--accent)] focus:outline-none';

export const Input = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement>>(
  function Input({ className, ...props }, ref) {
    return <input ref={ref} className={cn(CONTROL_CLASS, className)} {...props} />;
  },
);

export const Textarea = forwardRef<
  HTMLTextAreaElement,
  TextareaHTMLAttributes<HTMLTextAreaElement>
>(function Textarea({ className, ...props }, ref) {
  return (
    <textarea ref={ref} rows={3} className={cn(CONTROL_CLASS, 'resize-y', className)} {...props} />
  );
});

export function Select({
  className,
  children,
  ...props
}: SelectHTMLAttributes<HTMLSelectElement> & { children: ReactNode }) {
  return (
    <div className="relative">
      <select className={cn(CONTROL_CLASS, 'appearance-none pr-9', className)} {...props}>
        {children}
      </select>
      <span className="pointer-events-none absolute right-3.5 top-1/2 -translate-y-1/2 text-[var(--ink-muted)]">
        <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true">
          <path
            d="m2.5 4.5 3.5 3.5 3.5-3.5"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
          />
        </svg>
      </span>
    </div>
  );
}

/** The amount field: oversized, numeric keypad, currency symbol inline. */
export function AmountInput({
  value,
  onChange,
  currencySymbol,
  autoFocus,
}: {
  value: string;
  onChange: (value: string) => void;
  currencySymbol: string;
  autoFocus?: boolean;
}) {
  return (
    <div className="flex items-baseline gap-2 rounded-[var(--radius-card)] bg-[var(--surface-sunken)] px-4 py-4">
      <span className="numeric text-[22px] text-[var(--ink-secondary)]">{currencySymbol}</span>
      <input
        // `decimal` gives iOS a keypad with a decimal point; `numeric` does not.
        inputMode="decimal"
        autoFocus={autoFocus}
        value={value}
        onChange={(event) => onChange(event.target.value.replace(/[^\d.,]/g, ''))}
        placeholder="0.00"
        className="numeric w-full bg-transparent text-[34px] font-semibold text-[var(--ink)] outline-none placeholder:text-[var(--ink-muted)]"
        aria-label="Amount"
      />
    </div>
  );
}

export function Segmented<T extends string>({
  options,
  value,
  onChange,
  className,
}: {
  options: { value: T; label: string }[];
  value: T;
  onChange: (value: T) => void;
  className?: string;
}) {
  // One id per instance, so two segmented controls on a page do not share a
  // layout animation and slide into each other.
  const groupId = useId();

  return (
    <div
      role="tablist"
      className={cn(
        // `flex w-full`, not `inline-flex`: sizing to content meant three
        // ordinary labels ("Own money / HSA card / Something else") pushed the
        // control 78px past a 320px screen and 8px past a 390px one.
        'flex w-full rounded-[var(--radius-control)] bg-[var(--surface-sunken)] p-1',
        className,
      )}
    >
      {options.map((option) => {
        const active = option.value === value;
        return (
          <button
            key={option.value}
            role="tab"
            aria-selected={active}
            type="button"
            onClick={() => onChange(option.value)}
            className={cn(
              // `whitespace-nowrap`: Chinese labels are short enough to fit but
              // will happily break between characters if allowed to.
              // min-w-0 lets a segment shrink below its text; truncate keeps a
              // very long label from overflowing when it does.
              'relative min-w-0 flex-1 truncate rounded-[calc(var(--radius-control)-2px)] px-2 py-1.5 text-[13px] font-medium transition-colors sm:px-3.5',
              active ? 'text-[var(--ink)]' : 'text-[var(--ink-secondary)]',
            )}
          >
            {active ? (
              <motion.span
                layoutId={`segmented-${groupId}`}
                className="absolute inset-0 rounded-[calc(var(--radius-control)-2px)] bg-[var(--surface)] shadow-[0_1px_3px_rgba(0,0,0,0.12)]"
                transition={transitions.control}
              />
            ) : null}
            <span className="relative">{option.label}</span>
          </button>
        );
      })}
    </div>
  );
}

export function Switch({
  checked,
  onChange,
  label,
}: {
  checked: boolean;
  onChange: (checked: boolean) => void;
  label: string;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      onClick={() => onChange(!checked)}
      className={cn(
        'relative h-[31px] w-[51px] shrink-0 rounded-full transition-colors duration-200',
        checked ? 'bg-[var(--accent)]' : 'bg-[var(--line-strong)]',
      )}
    >
      <motion.span
        layout
        transition={transitions.control}
        className="absolute top-[2px] h-[27px] w-[27px] rounded-full bg-white shadow-sm"
        style={{ left: checked ? 22 : 2 }}
      />
    </button>
  );
}

export function Checkbox({
  checked,
  onChange,
  label,
}: {
  checked: boolean;
  onChange: (checked: boolean) => void;
  label: ReactNode;
}) {
  return (
    <button
      type="button"
      role="checkbox"
      aria-checked={checked}
      onClick={() => onChange(!checked)}
      className="flex items-center gap-2.5 text-left text-[14px] text-[var(--ink)]"
    >
      <span
        className={cn(
          'flex h-[22px] w-[22px] shrink-0 items-center justify-center rounded-[7px] border transition-colors',
          checked
            ? 'border-[var(--accent)] bg-[var(--accent)] text-[var(--accent-ink)]'
            : 'border-[var(--line-strong)]',
        )}
      >
        {checked ? <IconCheck size={14} strokeWidth={2.4} /> : null}
      </span>
      <span>{label}</span>
    </button>
  );
}

// --- Indicators -------------------------------------------------------------

export function Badge({
  children,
  tone = 'neutral',
  dotColor,
}: {
  children: ReactNode;
  tone?: 'neutral' | 'positive' | 'caution' | 'critical' | 'accent';
  dotColor?: string;
}) {
  const tones: Record<string, string> = {
    neutral: 'bg-[var(--surface-sunken)] text-[var(--ink-secondary)]',
    positive: 'bg-[var(--positive-soft)] text-[var(--positive)]',
    caution: 'bg-[var(--caution-soft)] text-[var(--caution)]',
    critical: 'bg-[var(--critical-soft)] text-[var(--critical)]',
    accent: 'bg-[var(--accent-soft)] text-[var(--accent)]',
  };
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[12px] font-medium',
        tones[tone],
      )}
    >
      {dotColor ? (
        <span
          className="h-[7px] w-[7px] rounded-full"
          style={{ background: dotColor }}
          aria-hidden="true"
        />
      ) : null}
      {children}
    </span>
  );
}

export function Avatar({
  name,
  color,
  size = 36,
}: {
  name: string;
  color?: string | null;
  size?: number;
}) {
  return (
    <span
      className="inline-flex shrink-0 items-center justify-center rounded-full font-medium text-white"
      style={{
        width: size,
        height: size,
        background: accentVar(color),
        fontSize: Math.round(size * 0.38),
      }}
      aria-hidden="true"
    >
      {initials(name)}
    </span>
  );
}

export function Skeleton({ className }: { className?: string }) {
  return (
    <div
      className={cn(
        'animate-pulse rounded-[var(--radius-control)] bg-[var(--surface-sunken)]',
        className,
      )}
      aria-hidden="true"
    />
  );
}

export function EmptyState({
  icon,
  title,
  body,
  action,
}: {
  icon?: ReactNode;
  title: string;
  body?: string;
  action?: ReactNode;
}) {
  return (
    <div className="flex flex-col items-center px-8 py-14 text-center">
      {icon ? <div className="mb-4 text-[var(--ink-muted)]">{icon}</div> : null}
      <h3 className="text-[17px] font-semibold text-[var(--ink)]">{title}</h3>
      {body ? (
        <p className="mt-1.5 max-w-xs text-[14px] leading-relaxed text-[var(--ink-secondary)]">
          {body}
        </p>
      ) : null}
      {action ? <div className="mt-5">{action}</div> : null}
    </div>
  );
}

// --- Sheet ------------------------------------------------------------------

/**
 * On a phone this is a bottom sheet that springs up; on a wide screen it is a
 * centerd dialog. Same component, because the content should not have to care.
 */
export function Sheet({
  open,
  onClose,
  title,
  children,
  footer,
  size = 'md',
}: {
  open: boolean;
  onClose: () => void;
  title?: string;
  children: ReactNode;
  footer?: ReactNode;
  size?: 'md' | 'lg';
}) {
  const { t } = useI18n();
  useScrollLock(open);

  useEffect(() => {
    if (!open) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (typeof document === 'undefined') return null;

  return createPortal(
    <AnimatePresence>
      {open ? (
        <div className="fixed inset-0 z-50 flex items-end justify-center sm:items-center">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={transitions.fade}
            onClick={onClose}
            className="absolute inset-0 bg-black/35 backdrop-blur-[2px]"
            aria-hidden="true"
          />
          <motion.div
            role="dialog"
            aria-modal="true"
            aria-label={title}
            initial={{ y: '100%', opacity: 0.6, scale: 1 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: '100%', opacity: 0.6 }}
            transition={transitions.sheet}
            className={cn(
              'relative flex max-h-[92dvh] w-full flex-col bg-[var(--surface)] shadow-[var(--shadow-sheet)]',
              'rounded-t-[var(--radius-sheet)] sm:rounded-[var(--radius-sheet)]',
              size === 'lg' ? 'sm:max-w-2xl' : 'sm:max-w-md',
            )}
          >
            {/* The grabber is what tells a phone user this panel can be dragged
                away — even before they try it. */}
            <div className="flex justify-center pt-2.5 sm:hidden">
              <span className="h-1 w-9 rounded-full bg-[var(--line-strong)]" />
            </div>

            {title ? (
              <div className="flex items-center justify-between gap-4 px-5 pb-3 pt-4">
                <h2 className="text-[17px] font-semibold tracking-tight text-[var(--ink)]">
                  {title}
                </h2>
                <button
                  type="button"
                  onClick={onClose}
                  aria-label={t('a11y.closeSheet')}
                  className="press -mr-1.5 rounded-full p-1.5 text-[var(--ink-muted)] hover:bg-[var(--surface-sunken)]"
                >
                  <IconClose size={20} />
                </button>
              </div>
            ) : null}

            <div className="min-h-0 flex-1 overflow-y-auto px-5 pb-5">{children}</div>

            {footer ? (
              <div className="safe-bottom border-t border-[var(--line)] bg-[var(--surface)] px-5 py-3.5">
                {footer}
              </div>
            ) : (
              <div className="safe-bottom" />
            )}
          </motion.div>
        </div>
      ) : null}
    </AnimatePresence>,
    document.body,
  );
}

export function ConfirmDialog({
  open,
  title,
  body,
  confirmLabel,
  destructive,
  extra,
  onConfirm,
  onCancel,
}: {
  open: boolean;
  title: string;
  body?: string;
  confirmLabel: string;
  destructive?: boolean;
  extra?: ReactNode;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  const { t } = useI18n();
  return (
    <Sheet open={open} onClose={onCancel} title={title}>
      {body ? (
        <p className="text-[14px] leading-relaxed text-[var(--ink-secondary)]">{body}</p>
      ) : null}
      {extra ? <div className="mt-4">{extra}</div> : null}
      <div className="mt-6 flex gap-2.5">
        <Button variant="secondary" fullWidth onClick={onCancel}>
          {t('common.cancel')}
        </Button>
        <Button variant={destructive ? 'destructive' : 'primary'} fullWidth onClick={onConfirm}>
          {confirmLabel}
        </Button>
      </div>
    </Sheet>
  );
}

// --- Toasts -----------------------------------------------------------------

type Toast = { id: number; message: string; tone: 'neutral' | 'positive' | 'critical' };
type ToastContextValue = { show: (message: string, tone?: Toast['tone']) => void };

const ToastContext = createContext<ToastContextValue | null>(null);

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const nextId = useRef(1);

  const show = useCallback((message: string, tone: Toast['tone'] = 'neutral') => {
    const id = nextId.current++;
    setToasts((current) => [...current, { id, message, tone }]);
    setTimeout(() => setToasts((current) => current.filter((toast) => toast.id !== id)), 3800);
  }, []);

  const value = useMemo(() => ({ show }), [show]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      {typeof document !== 'undefined'
        ? createPortal(
            <div
              // Toasts sit above the tab bar on mobile so they never cover it.
              className="pointer-events-none fixed inset-x-0 bottom-[calc(env(safe-area-inset-bottom,0px)+76px)] z-[60] flex flex-col items-center gap-2 px-4 sm:bottom-6"
              role="status"
              aria-live="polite"
            >
              <AnimatePresence>
                {toasts.map((toast) => (
                  <motion.div
                    key={toast.id}
                    initial={{ opacity: 0, y: 12, scale: 0.96 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: 8, scale: 0.97 }}
                    transition={transitions.pop}
                    className={cn(
                      'material-strong pointer-events-auto max-w-sm rounded-full px-4 py-2.5 text-[14px] shadow-[var(--shadow-pop)]',
                      toast.tone === 'critical'
                        ? 'text-[var(--critical)]'
                        : toast.tone === 'positive'
                          ? 'text-[var(--positive)]'
                          : 'text-[var(--ink)]',
                    )}
                  >
                    {toast.message}
                  </motion.div>
                ))}
              </AnimatePresence>
            </div>,
            document.body,
          )
        : null}
    </ToastContext.Provider>
  );
}

export function useToast(): ToastContextValue {
  const value = useContext(ToastContext);
  if (!value) throw new Error('useToast must be used inside <ToastProvider>');
  return value;
}

/** Copy-to-clipboard with the confirmation built in. */
export function CopyButton({ value, label }: { value: string; label?: string }) {
  const { t } = useI18n();
  const [copied, setCopied] = useState(false);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
    } catch {
      // Clipboard access needs a secure context; plenty of home servers run on
      // plain HTTP, so fall back to selecting the text for a manual copy.
      const field = document.createElement('textarea');
      field.value = value;
      document.body.appendChild(field);
      field.select();
      document.execCommand('copy');
      field.remove();
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <Button variant="secondary" size="sm" onClick={() => void copy()}>
      {copied ? t('common.copied') : (label ?? t('common.copy'))}
    </Button>
  );
}
